<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { useIntervalFn } from '@vueuse/core'
import { CheckCircle2, Info, XCircle } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import AppDialog from '@/components/AppDialog.vue'
import LoadingAnimation from '@/components/LoadingAnimation.vue'
import { Button } from '@/components/ui/button'
import { apiReportExportPath, apiReportExportsPath, downloadApiReportExportPath, reportsPath } from '@/routes'

type Status = 'idle' | 'pending' | 'processing' | 'completed' | 'failed'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

const status = ref<Status>('idle')
const errorMessage = ref<string | null>(null)
const open = ref(false)
const timedOut = ref(false)

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

const { pause, resume } = useIntervalFn(poll, POLL_INTERVAL_MS, { immediate: false })

let exportId: number | null = null
let pollStartedAt = 0

async function start(payload: {
  base_entity: string
  rule_tree: unknown
  columns: string[]
  sort: unknown
  count_date_range: unknown
}) {
  status.value = 'pending'
  errorMessage.value = null
  timedOut.value = false
  open.value = true
  pollStartedAt = Date.now()

  const res = await fetch(apiReportExportsPath(), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    status.value = 'failed'
    errorMessage.value = data.error ?? 'Failed to start export.'
    return
  }

  const data = (await res.json()) as { id: number }
  exportId = data.id
  status.value = 'processing'
  resume()
}

async function poll() {
  if (!exportId) return

  if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
    pause()
    status.value = 'failed'
    timedOut.value = true
    return
  }

  const res = await fetch(apiReportExportPath(exportId), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  const data = (await res.json()) as { status: Status; error_message: string | null; stalled: boolean }

  if (data.status === 'completed') {
    pause()
    status.value = 'completed'
    window.location.href = downloadApiReportExportPath(exportId)
  } else if (data.status === 'failed') {
    pause()
    status.value = 'failed'
    if (data.stalled) {
      timedOut.value = true
    } else {
      errorMessage.value = data.error_message ?? 'Export failed.'
    }
  }
}

function close() {
  pause()
  open.value = false
  status.value = 'idle'
  timedOut.value = false
  exportId = null
}

const isRunning = computed(() => status.value === 'pending' || status.value === 'processing')

defineExpose({ start })
</script>

<template>
  <AppDialog
    v-model:open="open"
    title="Exporting report"
    description="Generating a CSV of your report's full result set."
    :hide-close="isRunning"
    :prevent-close="isRunning"
  >
    <div v-if="isRunning" class="flex flex-col items-center gap-2 py-4 text-center">
      <LoadingAnimation :size="200" />
      <p class="text-sm font-medium">Generating your CSV export — please wait a bit.</p>
    </div>

    <div v-else-if="status === 'completed'" class="flex flex-col items-center gap-2 py-4 text-center">
      <CheckCircle2 class="text-primary size-10" />
      <p class="text-sm font-medium">Export ready — your download should start automatically.</p>
    </div>

    <div v-else-if="status === 'failed' && timedOut" class="flex flex-col items-center gap-2 py-4 text-center">
      <Info class="size-10 text-blue-600 dark:text-blue-400" />
      <p class="text-sm font-medium">This export is taking longer than expected.</p>
      <p class="text-muted-foreground text-sm">
        Check the <Link :href="reportsPath()" class="underline">Reports page</Link> for its latest status.
      </p>
    </div>

    <div v-else-if="status === 'failed'" class="flex flex-col items-center gap-2 py-4 text-center">
      <XCircle class="text-destructive size-10" />
      <p class="text-sm font-medium">{{ errorMessage }}</p>
    </div>

    <template #footer>
      <Button v-if="status === 'completed' || status === 'failed'" variant="outline" @click="close">Close</Button>
    </template>
  </AppDialog>
</template>
